import type { ZonePayload } from '../../net/protocol';

import { walkableTiles, tileCentre } from './layout';
import { cellHash } from './town';

/**
 * Ground decals: things painted flat on a combat zone's floor (2026-10-01).
 *
 * ── Why flat ────────────────────────────────────────────────────────────────
 *
 * The owner asked for *more decoration or obstacles in biomes in general*. The rubble in
 * `debris.ts` is the earlier answer, and it was switched off on the owner's word: *stone blocks
 * that you can go through is a bit weird*. Tiles are the server's unit of walkability, so nothing
 * smaller than a tile can be solid. A decal does not pretend to be: bones, a blood stain, ash, a
 * puddle, leaf litter, all lying in the floor's own plane, which a character crosses the way they
 * cross the floor. Real obstacles are the server's `OBSTACLE` tiles, dressed in `BIOME_OBSTACLES`.
 *
 * ── The art ─────────────────────────────────────────────────────────────────
 *
 * One 1024² atlas, `assets/decals/decals.webp`: sixteen 256² cells with straight alpha, fourteen
 * used. Generated with Flux on white and cut with BiRefNet; how is in
 * `doc/grind/architecture/GROUND-TEXTURES.md`. `DECAL_CELLS` is the address of each.
 *
 * ── Deterministic ───────────────────────────────────────────────────────────
 *
 * Placed from `cellHash` of the tile, never an RNG, for the reason `debris.ts` gives: the zone is
 * rebuilt on every arrival, and two players in one zone must see the same floor.
 */

/** Each decal's cell in the atlas, row-major from the top left. */
export const DECAL_CELLS = {
  crypts_bones: 0,
  crypts_stain: 1,
  caldera_ash: 2,
  caldera_vent: 3,
  abyss_crack: 4,
  abyss_rune: 5,
  wastes_skull: 6,
  wastes_skull_bones: 7,
  marsh_puddle: 8,
  marsh_moss: 9,
  thicket_leaves: 10,
  thicket_roots: 11,
  caverns_crystals: 12,
  caverns_puddle: 13,
} as const;

export type DecalId = keyof typeof DECAL_CELLS;

/** The atlas is `DECAL_GRID` × `DECAL_GRID` cells. */
export const DECAL_GRID = 4;

/** One kind of decal a biome scatters. */
export interface DecalSpec {
  id: DecalId;
  /** Diameter in world units, before a ±20% per-copy variation. */
  size: number;
  /**
   * How much it lights itself, as a fraction of its own texture: lava and void light that should
   * read in the dark. Absent is unlit. It does not bloom.
   */
  glow?: number;
}

/** One placed copy. */
export interface Decal {
  id: DecalId;
  x: number;
  z: number;
  yaw: number;
  size: number;
}

/**
 * The fraction of floor tiles that get a decal. Chosen by looking: enough that a room has two or
 * three, few enough that they read as incidents on the floor and not a second floor texture.
 */
export const DECAL_DENSITY = 0.22;

/** No decal within this of the entry or exit sigil, whose marks they would muddy. World units. */
export const DECAL_MARKER_CLEARANCE = 3.5;

/** How far from its tile's centre a decal may sit, as a fraction of `tileSize`. */
const DECAL_SPREAD = 0.6;

/** Where a zone's decals go. Empty for a biome without any. Pure. */
export function decalsFor(zone: ZonePayload, kinds: readonly DecalSpec[] | undefined, salt = 613): Decal[] {
  if (!kinds || kinds.length === 0) return [];
  const out: Decal[] = [];
  const clear2 = DECAL_MARKER_CLEARANCE * DECAL_MARKER_CLEARANCE;
  const near = (x: number, z: number, p: { x: number; z: number }) => (x - p.x) ** 2 + (z - p.z) ** 2 < clear2;

  for (const tile of walkableTiles(zone)) {
    const pick = cellHash(tile.col * salt + 1, tile.row * salt + 7);
    if (pick >= DECAL_DENSITY) continue;
    // Re-hashed, so position, size and kind do not move together.
    const h1 = cellHash(tile.row * salt + 3, tile.col * salt + 5);
    const h2 = cellHash(tile.col * 7 + tile.row * salt, tile.row * 11 + 13);
    const kind = kinds[Math.floor((pick / DECAL_DENSITY) * kinds.length) % kinds.length]!;
    const centre = tileCentre(zone, tile.col, tile.row);
    const x = centre.x + (h1 - 0.5) * zone.tileSize * DECAL_SPREAD;
    const z = centre.z + (h2 - 0.5) * zone.tileSize * DECAL_SPREAD;
    if (near(x, z, zone.spawn) || near(x, z, zone.exit)) continue;
    out.push({ id: kind.id, x, z, yaw: h1 * Math.PI * 2, size: kind.size * (0.8 + h2 * 0.4) });
  }
  return out;
}

/** The UV rectangle of a cell, for a texture loaded with Babylon's default `invertY`. Pure. */
export function decalUv(id: DecalId): { u0: number; v0: number; u1: number; v1: number } {
  const index = DECAL_CELLS[id];
  const col = index % DECAL_GRID;
  const row = Math.floor(index / DECAL_GRID);
  return {
    u0: col / DECAL_GRID,
    u1: (col + 1) / DECAL_GRID,
    v0: 1 - (row + 1) / DECAL_GRID,
    v1: 1 - row / DECAL_GRID,
  };
}
